import { format } from "date-fns";
import { CalendarIcon } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Calendar } from "@/components/ui/calendar";
import { Button } from "@/components/ui/button";
import { STATUS_META, STATUS_ORDER, SOURCES, CONTACT_TYPES } from "@/lib/status";
import { up } from "@/lib/text";

const Field = ({ label, children }) => (
  <div className="space-y-1.5">
    <Label className="text-xs uppercase tracking-wider text-muted-foreground">{label}</Label>
    {children}
  </div>
);

const DateField = ({ label, testid, value, onChange }) => (
  <Field label={label}>
    <Popover>
      <PopoverTrigger asChild>
        <Button data-testid={testid} variant="outline" className="w-full justify-start font-normal">
          <CalendarIcon className="mr-2 h-4 w-4" />
          {value ? value : <span className="text-muted-foreground">Pick a date</span>}
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-auto p-0" align="start">
        <Calendar mode="single" captionLayout="dropdown-buttons" fromYear={1940} toYear={2040}
          selected={value ? new Date(value) : undefined}
          onSelect={(d) => onChange(d ? format(d, "yyyy-MM-dd") : "")} />
      </PopoverContent>
    </Popover>
  </Field>
);

const PASSPORT_BLOCK = (form, set) => (
  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
    <DateField label="Date of Birth" testid="form-dob" value={form.date_of_birth || ""} onChange={(v) => set("date_of_birth", v)} />
    <Field label="Nationality"><Input data-testid="form-nationality" value={form.nationality || ""} onChange={(e) => set("nationality", e.target.value)} placeholder="South African" /></Field>
    <Field label="Passport Number"><Input data-testid="form-passport-number" value={form.passport_number || ""} onChange={(e) => set("passport_number", e.target.value)} placeholder="A1234567" /></Field>
    <div />
    <DateField label="Passport Issue Date" testid="form-passport-issue" value={form.passport_issue_date || ""} onChange={(v) => set("passport_issue_date", v)} />
    <DateField label="Passport Expiry Date" testid="form-passport-expiry" value={form.passport_expiry_date || ""} onChange={(v) => set("passport_expiry_date", v)} />
  </div>
);

export const CustomerForm = ({ form, setForm, consultants = [], isAdmin, showConsultant, showContactType = true }) => {
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  // CAPS standard for structured business fields (not email/URL/notes)
  const setUp = (k, v) => set(k, up(v));
  const isTeam = form.contact_type === "Team Member";

  const ContactTypeField = showContactType && (
    <Field label="Contact Type">
      <Select value={form.contact_type || "Customer"} onValueChange={(v) => set("contact_type", v)}>
        <SelectTrigger data-testid="form-contact-type"><SelectValue /></SelectTrigger>
        <SelectContent>
          {CONTACT_TYPES.map((t) => <SelectItem key={t} value={t}>{t}</SelectItem>)}
        </SelectContent>
      </Select>
    </Field>
  );

  // -------- Team Member simplified form --------
  if (isTeam) {
    return (
      <div className="space-y-5">
        {ContactTypeField}
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <Field label="Name"><Input data-testid="form-name" value={form.name} onChange={(e) => setUp("name", e.target.value)} placeholder="Julia" /></Field>
          <Field label="Position"><Input data-testid="form-position" value={form.position || ""} onChange={(e) => set("position", e.target.value)} placeholder="Sales Manager" /></Field>
          <Field label="Department"><Input data-testid="form-department" value={form.department || ""} onChange={(e) => set("department", e.target.value)} placeholder="Sales" /></Field>
          <Field label="Country"><Input data-testid="form-country" value={form.country} onChange={(e) => setUp("country", e.target.value)} placeholder="India" /></Field>
          <Field label="State / Province"><Input data-testid="form-state" value={form.state || ""} onChange={(e) => setUp("state", e.target.value)} placeholder="Gujarat" /></Field>
          <Field label="City"><Input data-testid="form-city" value={form.city || ""} onChange={(e) => setUp("city", e.target.value)} placeholder="Ahmedabad" /></Field>
          <Field label="Mobile Number"><Input data-testid="form-phone" value={form.phone} onChange={(e) => set("phone", e.target.value)} placeholder="+ ___________" /></Field>
          <Field label="WhatsApp Number"><Input data-testid="form-whatsapp" value={form.whatsapp} onChange={(e) => set("whatsapp", e.target.value)} placeholder="+ ___________" /></Field>
          <Field label="Email"><Input data-testid="form-email" value={form.email || ""} onChange={(e) => set("email", e.target.value)} placeholder="name@tsg.com" /></Field>
        </div>
        {PASSPORT_BLOCK(form, set)}
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 pt-2 border-t border-border">
          <Field label="LinkedIn"><Input data-testid="form-linkedin" value={form.linkedin} onChange={(e) => set("linkedin", e.target.value)} placeholder="https://linkedin.com/…" /></Field>
          <Field label="Instagram"><Input data-testid="form-instagram" value={form.instagram} onChange={(e) => set("instagram", e.target.value)} placeholder="https://instagram.com/…" /></Field>
        </div>
        <Field label="Notes"><Textarea data-testid="form-notes" value={form.notes || ""} onChange={(e) => set("notes", e.target.value)} rows={2} className="resize-none" /></Field>
      </div>
    );
  }

  // -------- Customer / Sub-Agent full form --------
  return (
    <div className="space-y-5">
      {ContactTypeField}

      <Field label="Conversation Summary">
        <Textarea data-testid="form-summary" value={form.summary} onChange={(e) => set("summary", e.target.value)} rows={3}
          placeholder="AI-generated summary of the conversation…" className="resize-none" />
      </Field>

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        <Field label="Customer Name"><Input data-testid="form-name" value={form.name} onChange={(e) => setUp("name", e.target.value)} placeholder="John Doe" /></Field>
        <Field label="Company"><Input data-testid="form-company" value={form.company} onChange={(e) => setUp("company", e.target.value)} placeholder="Acme Textiles" /></Field>
        <Field label="Phone"><Input data-testid="form-phone" value={form.phone} onChange={(e) => set("phone", e.target.value)} placeholder="+ ___________" /></Field>
        <Field label="WhatsApp"><Input data-testid="form-whatsapp" value={form.whatsapp} onChange={(e) => set("whatsapp", e.target.value)} placeholder="+ ___________" /></Field>
        <Field label="Country"><Input data-testid="form-country" value={form.country} onChange={(e) => setUp("country", e.target.value)} placeholder="South Africa" /></Field>
        <Field label="Lead Source">
          <Select value={form.lead_source || undefined} onValueChange={(v) => set("lead_source", v)}>
            <SelectTrigger data-testid="form-source"><SelectValue placeholder="Select source" /></SelectTrigger>
            <SelectContent>{SOURCES.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent>
          </Select>
        </Field>
        <Field label="State / Province"><Input data-testid="form-state" value={form.state || ""} onChange={(e) => setUp("state", e.target.value)} placeholder="Gauteng" /></Field>
        <Field label="City"><Input data-testid="form-city" value={form.city || ""} onChange={(e) => setUp("city", e.target.value)} placeholder="Johannesburg" /></Field>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        <Field label="Product Interest"><Input data-testid="form-product" value={form.product_interest} onChange={(e) => set("product_interest", e.target.value)} placeholder="15-head embroidery machine" /></Field>
        <Field label="Sub-Agent (local partner)"><Input data-testid="form-sub-agent" value={form.sub_agent || ""} onChange={(e) => set("sub_agent", e.target.value)} placeholder="Optional — for information only" /></Field>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        <Field label="Lead Status">
          <Select value={form.status} onValueChange={(v) => set("status", v)}>
            <SelectTrigger data-testid="form-status"><SelectValue /></SelectTrigger>
            <SelectContent>
              {STATUS_ORDER.map((s) => (
                <SelectItem key={s} value={s}>
                  <span className="flex items-center gap-2">
                    <span className="h-2 w-2 rounded-full" style={{ background: STATUS_META[s].color }} />
                    {STATUS_META[s].label}
                  </span>
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </Field>
        {form.status === "completed" ? (
          <Field label="PI Number / Proforma Invoice">
            <Input data-testid="form-pi-number" value={form.pi_number || ""} onChange={(e) => setUp("pi_number", e.target.value)} placeholder="PI-2026-0001" />
          </Field>
        ) : (
          <DateField label="Next Follow-up Date" testid="form-followup-date" value={form.next_followup_date || ""} onChange={(v) => set("next_followup_date", v)} />
        )}
      </div>

      {showConsultant && isAdmin && (
        <Field label="Responsible Consultant">
          <Select value={form.consultant_id || undefined} onValueChange={(v) => set("consultant_id", v)}>
            <SelectTrigger data-testid="form-consultant"><SelectValue placeholder="Assign consultant" /></SelectTrigger>
            <SelectContent>{consultants.map((c) => <SelectItem key={c.id} value={c.id}>{c.name} · {c.country || (c.role === "admin" ? "Admin" : "")}</SelectItem>)}</SelectContent>
          </Select>
        </Field>
      )}

      {/* Personal & Passport */}
      <div className="pt-2 border-t border-border">
        <p className="text-[11px] uppercase tracking-[0.2em] text-muted-foreground mb-3">Personal & Passport</p>
        {PASSPORT_BLOCK(form, set)}
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mt-4">
          <Field label="Designation"><Input data-testid="form-designation" value={form.designation || ""} onChange={(e) => set("designation", e.target.value)} placeholder="Managing Director" /></Field>
          <Field label="Email"><Input data-testid="form-email" value={form.email || ""} onChange={(e) => set("email", e.target.value)} placeholder="name@company.com" /></Field>
        </div>
        <div className="mt-4">
          <Field label="Notes"><Textarea data-testid="form-notes" value={form.notes || ""} onChange={(e) => set("notes", e.target.value)} rows={2} className="resize-none" placeholder="Anything worth remembering…" /></Field>
        </div>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 pt-2 border-t border-border">
        <Field label="Instagram"><Input data-testid="form-instagram" value={form.instagram} onChange={(e) => set("instagram", e.target.value)} placeholder="https://instagram.com/…" /></Field>
        <Field label="Facebook"><Input data-testid="form-facebook" value={form.facebook} onChange={(e) => set("facebook", e.target.value)} placeholder="https://facebook.com/…" /></Field>
        <Field label="LinkedIn"><Input data-testid="form-linkedin" value={form.linkedin} onChange={(e) => set("linkedin", e.target.value)} placeholder="https://linkedin.com/…" /></Field>
        <Field label="Website"><Input data-testid="form-website" value={form.website} onChange={(e) => set("website", e.target.value)} placeholder="https://…" /></Field>
      </div>
    </div>
  );
};
